/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
-----------------------------------------------------------------------------*/

<template>
  <div
    v-if="$store.getters.user && recording"
    class="row"
  >
    <h1
      id="evaluateRecording"
      class="sr-only"
    >
      Recording Results
    </h1>
    <div class="report-print-header">
      <svg
        class="svgicon icon-btc"
        style="height: 40px; width: 140px;"
      >
        <use xlink:href="/static/svg-symbols.svg#icon-btc" />
      </svg>
      <span v-if="!isShared">
        {{ reportPrefix }}: {{ recording.title }}<br>
        <small>(Recording dated {{ recording.createdDate | date }})</small>
      </span>
      <span v-else>
        {{ reportPrefix }}: {{ sharedGroup }} |
        {{ sharedUser }} | {{ recording.title }}<br>
        <small>(Recording dated {{ recording.createdDate | date }})</small>
      </span>
    </div>
    <section
      id="evaluate-main"
      class="col-md-8"
      aria-label="recording playback"
    >
      <div
        :ref="leftToolbarRef"
        class="row recording-left-header"
        aria-hidden="true"
        aria-labelledby="scoresheader"
      >
        <div
          class="col-sm-4 recording-select-div"
        >
          <span class="smaller-label">
            RECORDING
          </span><br>
          <recording-select
            v-if="!isShared"
            :recordings="recordings"
            :recording="recording"
            :is-small="true"
            @loadRecording="loadRecording"
          />
          <div
            v-else
            style="font-size: 10pt;"
          >
            {{ recording.title }}
          </div>
          <a
            class="sr-only sr-only-focusable"
            href="#"
            @click.prevent="skipToSummary"
          >
            Skip to recording scores
          </a>
        </div>
        <div
          v-if="isShared"
          class="col-sm-3"
        >
          <br>
          User:<br>
          <strong>{{ recording.userFirstName }} {{ recording.userLastName }}</strong>
        </div>
        <div
          v-else-if="activeGroups.length > 1 && (!assignmentsEnabled || recording.assignment)"
          class="col-sm-2"
        >
          <br>Group:<br>
          <strong>{{ sharedGroup }}</strong>
        </div>
        <div
          v-if="assignmentsEnabled && recording.assignment"
          class="col-sm-3"
        >
          <br>
          Assignment:<br>
          <strong>{{ recording.assignment.assignmentName }}</strong>
        </div>
        <div
          v-else-if="assignmentsEnabled && resultsLoaded"
          class="col-sm-3"
        >
          <span class="smaller-label">
            Move to Assignment
          </span><br>
          <assignment-select-simple
            id="rec-ass-select"
            :assignments="filteredAssignments"
            :is-small="true"
            @loadAssignment="moveRecording"
          />
        </div>
        <!-- If we have to show assignment, dont show date in narrow screens -->
        <div :class="assignmentsEnabled && activeGroups.length > 1 ? 'col-sm-3 recording-date' : 'col-sm-3'">
          <br>Recorded:<br>
          <strong>{{ recording.createdDate | date }}</strong>
        </div>
      </div>
      <div
        v-if="!recording"
        style="height:500px"
      />
      <div v-if="recording">
        <section aria-labelledby="evaluateRecording">
          <div class="row">
            <div style="margin-left: 9px;">
              <div id="videoPlayerMain">
                <!-- TODO: This whole section appears twice (depending on "isLargeViewport"); move to a component -->
                <section
                  v-if="!isLargeViewport && recording && recording.isVideo && recording.results && hasVideoPlaybackData && isVideo"
                  id="closeVideoContainer"
                  class="dont-print"
                >
                  <a
                    href="#"
                    @click.prevent="toggleVideoVisibility(true)"
                  >
                    Hide video
                    <svg
                      class="icon-chevron-up"
                    >
                      <use xlink:href="/static/svg-symbols.svg#icon-chevron-up" />
                    </svg>
                  </a>
                </section>
                <section
                  v-if="!isLargeViewport"
                  id="videoPlayerSidebar"
                  aria-label="video player"
                >
                  <div id="videoPlayerContainer">
                    <video
                      v-if="recording && recording.videoUrl && isVideo && hasVideoPlaybackData"
                      id="videoPlayer"
                      class="video-js vjs-default-skin"
                      playsinline
                    />
                  </div>
                </section>
                <section
                  v-if="!isLargeViewport && recording && isVideo && !hasVideoPlaybackData && recording.results && recording.results.tips"
                  id="multispeakerVideoLoading"
                  class="dont-print"
                >
                  <div>
                    Video playback will be ready shortly. Please wait.
                  </div>
                </section>
                <section
                  v-if="!isLargeViewport && recording && recording.isVideo && recording.results && hasVideoPlaybackData && !isVideo"
                  id="showVideoContainer"
                  class="dont-print"
                >
                  <a
                    href="#"
                    @click.prevent="toggleVideoVisibility(false)"
                  >
                    Show video
                    <svg
                      class="icon-chevron-down"
                    >
                      <use xlink:href="/static/svg-symbols.svg#icon-chevron-down" />
                    </svg>
                  </a>
                </section>
              </div>
              <div
                v-if="recording && hasPlaybackData"
                id="wavesurferContainer"
                :class="wavesurferContainerClass"
                @keypress="keyboardControls"
              >
                <div
                  v-if="wavesurferLoaded && speakerCounts.length >= 2 && recording.playbackUrl && (!isVideo || !hasVideoPlaybackData)"
                  id="speaker1ruler"
                  class="speakerRuler speakerIdentification"
                >
                  <speaker-label
                    :recording="recording"
                    speaker-num="1"
                    @speakerIdentified="speakerIdentified"
                  />
                </div>
                <div id="wavesurferBorder">
                  <div
                    v-for="spkr in speakerCounts"
                    v-if="wavesurferLoaded && spkr >= 2 && recording.playbackUrl && (!isVideo || !hasVideoPlaybackData)"
                    :id="`speaker${spkr}ruler`"
                    class="speakerRuler speakerIdentification"
                  >
                    <speaker-label
                      :recording="recording"
                      :speaker-num="spkr"
                      @speakerIdentified="speakerIdentified"
                    />
                  </div>

                  <div
                    id="wavesurferTimeline"
                    aria-hidden="true"
                  />
                  <div
                    v-if="wavesurferLoaded && resultsLoaded"
                    :key="stickyKey"
                  >
                    <comment-sticky
                      v-for="comment in feedbackRegions"
                      :key="comment.feedbackId"
                      :recording="recording"
                      :wavesurfer="wavesurfer"
                      :comment="comment"
                      :speaker-num="speakerNum"
                    />
                  </div>
                  <div
                    id="wavesurferContainerInternal"
                    class="wavesurfer"
                    tabindex="0"
                    aria-label="Image of the recording. When selected, press p to play or pause. Press f to fast forward. Press r to rewind."
                  />
                </div>
                <div id="wavesurferPlayButtonContainerOuter">
                  <div id="wavesurferPlayButtonContainerInner">
                    <span
                      id="wavesurferPlay"
                      tabindex="0"
                      role="button"
                      aria-label="Play"
                      class="wavesurferButton"
                      title="Play"
                      @click.prevent="wsPlay"
                    >
                      <svg
                        class="svgicon icon-soundplay"
                      >
                        <use xlink:href="/static/svg-symbols.svg#icon-soundplay" />
                      </svg>
                    </span>
                    <span
                      id="wavesurferPause"
                      tabindex="0"
                      role="button"
                      aria-label="Pause"
                      class="wavesurferButton"
                      style="display: none;"
                      title="Pause"
                      @click.prevent="wsPause"
                    >
                      <svg class="svgicon icon-soundpause">
                        <use xlink:href="/static/svg-symbols.svg#icon-soundpause" />
                      </svg>
                    </span>
                    <span
                      id="wavesurferRestart"
                      tabindex="0"
                      role="button"
                      aria-label="Rewind"
                      class="wavesurferButton"
                      title="Rewind"
                      @click.prevent="wsRestart"
                    >
                      <svg class="svgicon icon-soundbegining">
                        <use xlink:href="/static/svg-symbols.svg#icon-soundbegining" />
                      </svg>
                    </span>
                  </div>
                  <div
                    id="wavesurferTimeContainer"
                    aria-hidden="true"
                  >
                    <span
                      id="wavesurferCurrentTime"
                      aria-label="Playback position"
                    >
                      00:00.0
                    </span>
                    /
                    <span
                      id="wavesurferTotalTime"
                      aria-label="Audio length"
                    >
                      <svg
                        class="icon-refresh rotate"
                        style="width: 14px; height: 14px;"
                      >
                        <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
                      </svg>
                    </span> &nbsp;
                  </div>

                  <div id="wavesurferRecordButtonContainer">
                    <div
                      id="wavesurferRecord"
                      class="wavesurferButton"
                      title="Record"
                      @click.prevent="wsRecord"
                    >
                      <svg class="svgicon icon-record">
                        <use xlink:href="/static/svg-symbols.svg#icon-record" />
                      </svg>
                      <span
                        class="record-text"
                        tabindex="0"
                        role="button"
                        aria-label="Record"
                      >
                        RECORD
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <recording-scorecard
            :key="scorecardKey"
            :recording="recording"
          />

          <recording-status
            :key="statusKey"
            :recording="recording"
          />
          <div
            v-if="recording && resultsLoaded && isConversational && hasPlaybackData"
            :key="mainKey"
          >
            <div
              class="col-md-12"
            >
              <span class="viewResultsHeader">
                View Results For:
              </span>
              <label
                v-for="count in speakerCounts"
                :for="'speaker' + count"
                class="speakerLabel"
              >
                <input
                  :id="'speaker' + count"
                  v-model="speakerNum"
                  tabindex="0"
                  type="radio"
                  name="speaker"
                  :value="count - 1"
                  @change.prevent="speakerChanged"
                >
                <span
                  v-if="count == recording.speakerNum"
                >
                  {{ recording.userFirstName }}
                </span>
                <span v-else>
                  Speaker {{ count }}
                </span>
              </label>
            </div>

            <recording-metrics-conversational
              v-if="recording.results && recording.results.scores && recording.results.scores.perSpeaker && recording.results.scores.perSpeaker[speakerNum] && !recording.results.scores.perSpeaker[speakerNum].isEmptyResults"
              :key="chartsKey"
              :recording="recording"
              :wavesurfer="wavesurfer"
              :speaker-num="speakerNum"
            />
            <div v-else>
              <br><br>
              There were no metrics calculated for this speaker
            </div>
            <recording-vibes
              v-if="recording.results && recording.results.scores && recording.results.scores.perSpeaker && recording.results.scores.perSpeaker[speakerNum] && !recording.results.scores.perSpeaker[speakerNum].isEmptyResults"
              :key="vibesKey"
              :recording="recording"
              :wavesurfer="wavesurfer"
              :speaker-num="speakerNum"
            />
          </div>

          <recording-metrics
            v-if="resultsLoaded && !isConversational"
            :key="chartsKey"
            :recording="recording"
            :wavesurfer="wavesurfer"
            :speaker-num="speakerNum"
          />

          <recording-vibes
            v-if="resultsLoaded && scores.vibes && !isConversational"
            :key="vibesKey"
            :recording="recording"
            :wavesurfer="wavesurfer"
            :speaker-num="speakerNum"
          />
          <br><br>
          <div
            id="scroll-buffer"
            style="display: none;"
          >
            <!-- This needs to match height of Wavesurfer when it docks to avoid issues -->
            <br><br><br><br><br><br>
          </div>
        </section>
      </div>
    </section>
    <div
      class="col-md-4"
      style="margin-top: 0; padding-top: 0"
    >
      <div
        v-if="showSharedWithGroup || showSubmittedForReview"
        :ref="rightToolbarRef"
        class="row recording-right-header header-sharing-parent-div"
      >
        <div
          v-if="showSharedWithGroup"
          class="col-sm-4 share-with-entire-group"
        >
          <div class="header-sharing-option icon-cell">
            <svg class="svgicon icon-users">
              <use xlink:href="/static/svg-symbols.svg#icon-users" />
            </svg>
          </div>
          <div class="header-sharing-option">
            <span
              v-if="recording.feedbackRequested"
            >
              Shared with<br>Entire Group
            </span>
            <a
              v-else
              href="#"
              @click.prevent="updateRecording({recordingId: recording.recordingId, feedbackRequested: true})"
            >
              Share with<br>Entire Group
            </a>
          </div>
          <div
            v-if="recording.feedbackRequested"
            class="header-sharing-option"
          >
            <a
              href="#"
              @click.prevent="updateRecording({recordingId: recording.recordingId, feedbackRequested: false})"
            >
              Undo
            </a>
          </div>
        </div>
        <div
          v-if="showSubmittedForReview"
          :class="showSharedWithGroup ? 'col-sm-3 header-sharing-parent-div submitted-for-review-div' : 'col-sm-3 header-sharing-parent-div submitted-for-review-div header-sharing-margined'"
        >
          <div class="header-sharing-option icon-cell">
            <svg class="svgicon icon-flag">
              <use xlink:href="/static/svg-symbols.svg#icon-flag" />
            </svg>
          </div>
          <div class="header-sharing-option">
            <span
              v-if="recording.submitted"
            >
              Flagged<br>for Review
            </span>
            <a
              v-else
              href="#"
              @click.prevent="updateRecording({recordingId: recording.recordingId, submitted: true})"
            >
              Flag<br>for Review
            </a>
          </div>
          <div
            v-if="recording.submitted"
            class="header-sharing-option"
          >
            <a
              href="#"
              @click.prevent="updateRecording({recordingId: recording.recordingId, submitted: false})"
            >
              Undo
            </a>
          </div>
        </div>
        <div
          class="col-sm-3 comment-total"
        >
          <div class="comment-count">
            {{ commentCount }}
          </div>
          <div class="comment-count-title">
            Total<br>
            Comment{{ commentCount !== 1 ? 's' : '' }}
          </div>
        </div>
        <!-- Here we add a spacer when sharing is only half on to improve how it looks -->
        <div
          v-if="showSubmittedForReview && !showSharedWithGroup"
          class="col-sm-4 header-sharing-spacer"
        />
        <div
          v-if="showSubmittedForReview"
          class="col-sm-2 recording-reviewed"
        >
          REVIEWED<br>
          <div :class="isReviewed ? 'reviewed-ok' : 'reviewed-pending'" />
        </div>
      </div>

      <!-- TODO: This whole section appears twice (depending on "isLargeViewport"); move to a component -->
      <section
        v-if="isLargeViewport && recording && recording.isVideo && recording.results && hasVideoPlaybackData && isVideo"
        id="closeVideoContainer"
        class="dont-print"
      >
        <a
          href="#"
          @click.prevent="toggleVideoVisibility(true)"
        >
          Hide video
          <svg
            class="icon-chevron-up"
          >
            <use xlink:href="/static/svg-symbols.svg#icon-chevron-up" />
          </svg>
        </a>
      </section>
      <section
        id="videoPlayerSidebar"
        aria-label="video player"
      >
        <div id="videoPlayerContainer">
          <video
            v-if="isLargeViewport && recording && recording.videoUrl && isVideo && hasVideoPlaybackData"
            id="videoPlayer"
            class="video-js vjs-default-skin"
            playsinline
          />
        </div>
      </section>
      <section
        v-if="isLargeViewport && recording && isVideo && !hasVideoPlaybackData && recording.results && recording.results.tips"
        id="multispeakerVideoLoading"
        class="dont-print"
      >
        <div>
          Video playback will be ready shortly. Please wait.
        </div>
      </section>
      <section
        v-if="isLargeViewport && recording && recording.isVideo && recording.results && hasVideoPlaybackData && !isVideo"
        id="showVideoContainer"
        class="dont-print"
      >
        <a
          href="#"
          @click.prevent="toggleVideoVisibility(false)"
        >
          Show video
          <svg
            class="icon-chevron-down"
          >
            <use xlink:href="/static/svg-symbols.svg#icon-chevron-down" />
          </svg>
        </a>
      </section>

      <section
        v-if="recording && (recording.results && recording.results.tips) || recording.diarizedTranscript"
        aria-label="recording results"
      >
        <evaluate-tips
          id="evaluate-tips"
          :key="tipsKey"
          :recording="recording"
          :wavesurfer="wavesurfer"
          :speaker-num="speakerNum"
        />
      </section>
      <br>
      <section
        v-if="recording && feedbackActive"
        aria-label="recording feedback"
      >
        <feedback-form
          v-if="recording.results && recording.results.scores"
          id="feedback-sidebar"
          :recording="recording"
          :active-group="activeGroup"
          :wavesurfer="wavesurfer"
          :is-group-manager="isGroupManager"
          :is-my-recording="isMyRecording"
          @feedbackModified="feedbackModified"
        />
      </section>
    </div>
  </div>
</template>

<style lang="less">
  @import '../../../node_modules/tippy.js/dist/tippy.css';
  @import '../../../node_modules/video.js/dist/video-js.min.css';
  @import '../../less/evaluate-recording.less';
</style>

<script>
import EvaluateRecordingJs from '../specials/EvaluateRecordingJs';
import WavesurferJs from '../specials/WavesurferJs';
import RecordingScorecard from '../layout/RecordingScorecard';

export default {
  components: {RecordingScorecard},
  mixins: [EvaluateRecordingJs, WavesurferJs],
};
</script>
